import { Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { dismissAgentAnnotation } from "@/lib/dismiss-agent-annotation";
import { useT } from "@/i18n";
import { cursorsMessages } from "@/i18n/messages/cursors";
import { useWindowOrigin } from "@/desktop/use-window-origin";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import {
  agentCursorAnnotationTargets,
  annotationGeometry,
  cursorOwnsAnnotationCallout,
  type AgentAnnotationTarget,
  type AnnotationGeometry,
} from "@/lib/agent-annotation";
import type { AgentCursor as AgentCursorData } from "@/stores/canvas-store";
import {
  annotationInkOverflow,
  annotationSketchSeed,
} from "@/lib/annotation-sketch";
import { useAgentInk } from "@/stores/ink-settings-store";
import type { NoteRect } from "@/lib/handwritten-callout-placement";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { agentCursorDisplayStatus } from "./agent-cursor-display-status";
import { HandwrittenCallout } from "./handwritten-callout";
import { PresentationGuideControls } from "./presentation-guide-controls";
import { SketchMarkSvg } from "./sketch-mark-svg";
import { useAnnotationHover } from "./use-annotation-hover";
import { prefersReducedMotion } from "@/lib/reveal-motion";

interface AgentAnnotationOverlayProps {
  target?: AgentAnnotationTarget;
  testId: string;
  onSettled?: () => void;
  showCallout?: boolean;
  onDismiss?: () => void;
}

/** The mark's display-local content box (the ink overflow excluded). */
function markRect(geometry: AnnotationGeometry): NoteRect {
  const left = geometry.left + geometry.contentLeft;
  const top = geometry.top + geometry.contentTop;
  return {
    left,
    top,
    right: left + geometry.contentWidth,
    bottom: top + geometry.contentHeight,
  };
}

/** Display-local tail and tip of an arrow mark. */
function arrowEnds(
  target: AgentAnnotationTarget,
  origin: { x: number; y: number },
) {
  if (target.shape !== "path" && target.shape !== "polyline") return undefined;
  const tail = target.path[0];
  const tip = target.path[target.path.length - 1];
  if (!target.arrowEnd || !tail || !tip) return undefined;
  const local = (point: { x: number; y: number }) => ({
    x: target.globalX + point.x * target.width - origin.x,
    y: target.globalY + point.y * target.height - origin.y,
  });
  return { tail: local(tail), tip: local(tip) };
}

export function AgentAnnotationOverlays({
  cursor,
  variant,
  standalone = false,
}: {
  cursor: AgentCursorData;
  variant: "workspace" | "compact";
  standalone?: boolean;
}) {
  const displayStatus = agentCursorDisplayStatus(cursor);
  const baseTestId =
    variant === "compact"
      ? "desktop-agent-area"
      : `agent-area-${cursor.agentId}`;
  return (
    <>
      {agentCursorAnnotationTargets(cursor).map((target, index) => (
        <AgentAnnotationOverlay
          key={target.annotationId ?? target.seq}
          target={target}
          showCallout={
            !(
              target.executionId === cursor.executionId &&
              (displayStatus.error || displayStatus.question)
            ) &&
            !(
              !standalone &&
              cursor.presentationDeliveryId &&
              cursor.presentationNarration?.trim() ===
                target.callout?.content.trim() &&
              cursorOwnsAnnotationCallout(cursor, target)
            )
          }
          onDismiss={() => dismissAgentAnnotation(cursor.agentId, target)}
          testId={
            target === cursor.annotationTarget
              ? baseTestId
              : `${baseTestId}-retained-${index}`
          }
        />
      ))}
      <PresentationGuideControls cursor={cursor} />
    </>
  );
}

export function AgentAnnotationOverlay({
  target,
  testId,
  onSettled,
  showCallout = true,
  onDismiss,
}: AgentAnnotationOverlayProps) {
  const t = useT(cursorsMessages);
  const origin = useWindowOrigin();
  const ink = useAgentInk();
  const displayId = getWindowDisplayId();
  const reducedMotion = prefersReducedMotion();
  const snap = !target || target.transition === "instant" || reducedMotion;
  const [visible, setVisible] = useState(snap);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const identity = target ? (target.annotationId ?? String(target.seq)) : null;
  const dismissed =
    !!target?.dismissed || (identity !== null && dismissedKey === identity);
  const settledTargetRef = useRef<string | null>(null);
  const targetKey = target
    ? `${target.annotationId ?? ""}:${target.seq}`
    : null;
  const notifySettled = useCallback(() => {
    if (!targetKey || settledTargetRef.current === targetKey) return;
    settledTargetRef.current = targetKey;
    onSettled?.();
  }, [onSettled, targetKey]);
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const geometry = useMemo(
    () =>
      target &&
      origin.ready &&
      (displayId === null || target.displayId === displayId)
        ? annotationGeometry(
            target,
            { x: origin.x, y: origin.y },
            { width: viewportWidth, height: viewportHeight },
            annotationInkOverflow(target),
          )
        : null,
    [
      target,
      origin.ready,
      origin.x,
      origin.y,
      displayId,
      viewportWidth,
      viewportHeight,
    ],
  );
  const renderEligible = geometry !== null;
  const closeLeft = geometry
    ? Math.max(4, Math.min(geometry.right - 12, window.innerWidth - 28))
    : 0;
  const closeTop = geometry ? Math.max(4, geometry.top - 12) : 0;
  const callout = showCallout ? target?.callout : undefined;
  const [noteBounds, setNoteBounds] = useState<NoteRect | null>(null);
  const note = callout ? noteBounds : null;
  const hovered = useAnnotationHover(
    geometry && !dismissed
      ? {
          left: Math.min(geometry.left, closeLeft, note?.left ?? Infinity),
          top: Math.min(geometry.top, closeTop, note?.top ?? Infinity),
          right: Math.max(geometry.right, closeLeft + 28, note?.right ?? 0),
          bottom: Math.max(geometry.bottom, closeTop + 28, note?.bottom ?? 0),
        }
      : null,
    origin,
    target?.displayId,
  );
  const [closeFocused, setCloseFocused] = useState(false);

  useEffect(() => {
    if (!renderEligible) {
      setVisible(false);
      return;
    }
    if (snap) {
      setVisible(true);
      return;
    }
    setVisible(false);
    // Paint the hidden stroke before starting its CSS transition. One frame
    // can run before the initial paint and collapse both states into one.
    let frame = window.requestAnimationFrame(() => {
      frame = window.requestAnimationFrame(() => setVisible(true));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [renderEligible, snap, target?.seq]);

  useEffect(() => {
    if (!renderEligible || !visible) return;
    if (snap) {
      notifySettled();
      return;
    }
    const timer = window.setTimeout(
      notifySettled,
      target?.transitionDurationMs ?? 200,
    );
    return () => window.clearTimeout(timer);
  }, [
    notifySettled,
    renderEligible,
    snap,
    target?.transitionDurationMs,
    visible,
  ]);

  if (!target || !geometry || dismissed) return null;

  return (
    <>
      <SketchMarkSvg
        target={target}
        geometry={geometry}
        ink={ink}
        testId={testId}
        visible={visible}
        snap={snap}
        onOpacitySettled={notifySettled}
      />
      <Button
        variant="secondary"
        size="iconSm"
        aria-label={t("closeAnnotation")}
        title={t("closeAnnotation")}
        data-testid={`${testId}-dismiss`}
        data-interactive="true"
        className="pointer-events-auto fixed z-[10000]"
        style={{
          left: closeLeft,
          top: closeTop,
          opacity: hovered || closeFocused ? 1 : 0,
          pointerEvents: hovered || closeFocused ? "auto" : "none",
        }}
        onFocus={() => setCloseFocused(true)}
        onBlur={() => setCloseFocused(false)}
        onClick={(event) => {
          event.stopPropagation();
          setDismissedKey(identity);
          onDismiss?.();
        }}
      >
        <Icons.X size={14} />
      </Button>
      {callout ? (
        <HandwrittenCallout
          annotationId={target.annotationId}
          content={callout.content}
          role={callout.role}
          ink={ink}
          seed={annotationSketchSeed(target)}
          mark={markRect(geometry)}
          arrow={arrowEnds(target, origin)}
          onBoundsChange={setNoteBounds}
          testId={testId}
        />
      ) : null}
    </>
  );
}
